import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal'; // Yeni modalı import et

// Types (Permission, Role) - Bunları ayrı bir types dosyasından import etmek daha iyi olabilir
interface Permission {
  resource: string;
  actions: string[];
}

interface Role {
  _id: string;
  name: string;
  roleCode: string;
  permissions: Permission[];
}

// Tüm mevcut kaynakların ve eylemlerin tanımları (ManageRoles.tsx'den alındı)
const allResources = [
    { id: 'products', name: 'Ürünler', description: 'Ürün yönetimi', icon: '📦' },
    { id: 'orders', name: 'Siparişler', description: 'Sipariş yönetimi', icon: '🛒' },
    { id: 'colors', name: 'Renkler', description: 'Renk yönetimi', icon: '🎨' },
    { id: 'fabrics', name: 'Kumaşlar', description: 'Kumaş yönetimi', icon: '🧵' },
    { id: 'accessories', name: 'Aksesuarlar', description: 'Aksesuar yönetimi', icon: '✨' },
    { id: 'vendors', name: 'Üreticiler', description: 'Tedarikçi yönetimi', icon: '🏭' },
   /* { id: 'designer', name: 'Tasarımcı Paneli', description: 'Tasarımcı alanına erişim', icon: '👩‍🎨' },
    { id: 'sourcing', name: 'Sourcing Paneli', description: 'Sourcing alanına erişim', icon: '🔍' },
    { id: 'modelist', name: 'Modelist Paneli', description: 'Modelist alanına erişim', icon: '✂️' },
    { id: 'planlamacı', name: 'Planlama Paneli', description: 'Planlama alanına erişim', icon: '📆' },
    { id: 'admin', name: 'Admin Paneli', description: 'Admin alanına erişim', icon: '👑' }, */
    { id: 'fitcrt', name: 'Fit Kritik', description: 'Fit kritik işlemleri', icon: '👕' },
    // Yeni kaynaklar buraya eklenebilir
];

const allActions = [
    { id: 'read', name: 'Görüntüleme', description: 'İçeriği görüntüleme', icon: '👁️' },
    { id: 'create', name: 'Oluşturma', description: 'Yeni içerik oluşturma', icon: '➕' },
    { id: 'update', name: 'Düzenleme', description: 'Mevcut içeriği düzenleme', icon: '✏️' },
    { id: 'delete', name: 'Silme', description: 'İçerik silme', icon: '🗑️' }
    // Yeni eylemler buraya eklenebilir
];


const RolesPage: React.FC = () => {
  const { isAdmin } = useContext(AuthContext);
  const [roles, setRoles] = useState<Role[]>([]);

  // Rol düzenleme için state
  const [isEditingRole, setIsEditingRole] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState<string>('');
  const [roleCode, setRoleCode] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);

  // Yeni rol ekleme için state
  const [isAddingRole, setIsAddingRole] = useState<boolean>(false);
  const [newRoleName, setNewRoleName] = useState<string>('');
  const [newRoleCode, setNewRoleCode] = useState<string>('');

  // Genel state
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingAction, setLoadingAction] = useState<string>(''); // Hangi aksiyonun çalıştığını takip etmek için (örn: 'save-role', 'delete-role-123')

  // Arama ve filtreleme için state
  const [roleSearchQuery, setRoleSearchQuery] = useState<string>('');

  // Confirmation Modal State
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalProps, setConfirmModalProps] = useState<{
    title: string;
    message: React.ReactNode;
    onConfirm: () => void;
    confirmButtonVariant?: 'primary' | 'danger' | 'warning';
    confirmText?: string;
  }>({
    title: '',
    message: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    if (!isAdmin) return;
    fetchRoles();
  }, [isAdmin]);

  const fetchRoles = async () => {
    setLoading(true);
    setError('');
    try {
      const rolesResponse = await api.get('/roles');
      setRoles(rolesResponse.data);
    } catch (err) {
      setError('Roller yüklenirken bir hata oluştu.');
      console.error("Fetch roles error:", err);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Hata ve başarı mesajlarını temizleme fonksiyonu
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Hata ve başarı mesajlarını ayarlama ve otomatik temizleme
  const setFlashMessage = (type: 'error' | 'success', message: string, duration: number = 5000) => {
    clearMessages();
    if (type === 'error') setError(message);
    if (type === 'success') setSuccess(message);
    setTimeout(clearMessages, duration);
  };

  // Rol düzenleme modunu aç
  const handleEditRole = (role: Role) => {
    clearMessages();
    setEditingRole(role);
    setRoleName(role.name);
    setRoleCode(role.roleCode);
    // İzinleri kopyalarken derin kopya oluşturduğunuzdan emin olun
    setRolePermissions(role.permissions ? JSON.parse(JSON.stringify(role.permissions)) : []);
    setIsEditingRole(true);
    setIsAddingRole(false); // Aynı anda hem ekleme hem düzenleme olmasın
  };

  // Rol düzenleme modunu kapat
  const handleCancelEditRole = () => {
    setIsEditingRole(false);
    setEditingRole(null);
    setRoleName('');
    setRoleCode('');
    setRolePermissions([]);
    clearMessages();
  };

  // Güncellenen rolü kaydet
  const handleSaveRole = async () => {
    if (!editingRole) return;

    if (!roleName) { // Rol kodu değiştirilemez olduğu için sadece adı kontrol ediyoruz
      setFlashMessage('error', 'Rol adı zorunludur.');
      return;
    }

    setLoadingAction(`save-${editingRole._id}`);
    clearMessages();
    try {
      await api.put(`/roles/${editingRole._id}`, {
        name: roleName,
        // roleCode gönderilmiyor, backend'de değiştirilmemeli
        permissions: rolePermissions,
      });
      setFlashMessage('success', 'Rol başarıyla güncellendi.', 3000);
      fetchRoles(); // Listeyi güncelle
      handleCancelEditRole(); // Modalı kapat
    } catch (err: any) {
      console.error("Save role error:", err);
      const errorMessage = err.response?.data?.message || 'Rol güncellenirken bir hata oluştu.';
      setFlashMessage('error', errorMessage);
    } finally {
      setLoadingAction('');
    }
  };

  // İzin kontrolü (Düzenleme modalı için)
  const hasPermission = (resource: string, action: string): boolean => {
    const permission = rolePermissions.find(p => p.resource === resource);
    return permission ? permission.actions.includes(action) : false;
  };

  // İzin aç/kapat (Düzenleme modalı için)
  const togglePermission = (resource: string, action: string) => {
    setRolePermissions(prevPermissions => {
        const updatedPermissions = JSON.parse(JSON.stringify(prevPermissions)); // Derin kopya
        let permission = updatedPermissions.find((p: Permission) => p.resource === resource);

        if (!permission) {
            // Kaynak için izin yoksa, oluştur ve eylemi ekle
            permission = { resource, actions: [action] };
            updatedPermissions.push(permission);
        } else {
            const actionIndex = permission.actions.indexOf(action);
            if (actionIndex > -1) {
                // Eylem varsa, kaldır
                permission.actions.splice(actionIndex, 1);
                // Eğer bu kaynak için başka eylem kalmadıysa, kaynağı tamamen kaldır
                if (permission.actions.length === 0) {
                    const permissionIndex = updatedPermissions.findIndex((p: Permission) => p.resource === resource);
                    if (permissionIndex > -1) {
                        updatedPermissions.splice(permissionIndex, 1);
                    }
                }
            } else {
                // Eylem yoksa, ekle
                permission.actions.push(action);
            }
        }
        return updatedPermissions;
    });
};


  // Yeni rol ekleme formunu aç
  const handleAddRole = () => {
    clearMessages();
    setIsAddingRole(true);
    setIsEditingRole(false); // Düzenleme modalını kapat
    setNewRoleName('');
    setNewRoleCode(''); // Başlangıçta boş olsun, kullanıcı yazınca otomatik dolsun
  };

  // Yeni rol oluşturma API isteği
  const createNewRole = async () => {
    if (!newRoleName || !newRoleCode) {
      setFlashMessage('error', 'Rol adı ve kodu zorunludur.');
      return;
    }

    // Rol kodunun benzersizliğini kontrol et (isteğe bağlı, backend yapmalı)
    if (roles.some(role => role.roleCode === newRoleCode)) {
        setFlashMessage('error', 'Bu rol kodu zaten kullanılıyor.');
        return;
    }

    setLoadingAction('create-role');
    clearMessages();
    try {
      const response = await api.post('/roles', {
        name: newRoleName,
        roleCode: newRoleCode,
        permissions: [], // Başlangıçta boş izinler
      });
      setFlashMessage('success', 'Yeni rol başarıyla oluşturuldu. Düzenleme ekranı açılıyor...', 4000); // Mesajı güncelle
      await fetchRoles(); // Listeyi güncelle (handleEditRole'dan önce çağırılabilir veya sonra)
      setIsAddingRole(false); // Formu kapat
      setNewRoleName('');
      setNewRoleCode('');

      // İsteğe bağlı: Oluşturulan rolü hemen düzenlemeye aç
      handleEditRole(response.data); // Yeni eklenen satır

    } catch (err: any) {
      console.error("Create role error:", err);
      const errorMessage = err.response?.data?.message || 'Rol oluşturulurken bir hata oluştu.';
      setFlashMessage('error', errorMessage);
    } finally {
      setLoadingAction('');
    }
  };

  // Rol silme işlemini başlatan fonksiyon (Modalı açar)
  const handleDeleteRole = (roleId: string, roleName: string) => {
    setConfirmModalProps({
      title: 'Rol Silme Onayı',
      message: (
        <>
          <strong>'{roleName}'</strong> rolünü silmek istediğinizden emin misiniz?
          <br />
          <span className="text-red-600 font-medium">Bu işlem geri alınamaz.</span>
        </>
      ),
      onConfirm: () => executeDeleteRole(roleId, roleName), // Onaylandığında çalışacak fonksiyon
      confirmButtonVariant: 'danger',
      confirmText: 'Evet, Sil',
    });
    setIsConfirmModalOpen(true);
  };

  // Modal onaylandıktan sonra rol silme işlemini gerçekleştiren fonksiyon
  const executeDeleteRole = async (roleId: string, roleName: string) => {
    setLoadingAction(`delete-${roleId}`); // Loading state'ini modal içinde yönetmek yerine burada başlatabiliriz
    // İsteğe bağlı: Modalın içindeki butonu loading yapmak için modal state'ini güncelleyebiliriz
    // setConfirmModalProps(prev => ({ ...prev, isLoading: true }));

    clearMessages();
    try {
      await api.delete(`/roles/${roleId}`);
      setFlashMessage('success', `'${roleName}' rolü başarıyla silindi.`, 3000);
      fetchRoles(); // Listeyi güncelle
      // Eğer silinen rol düzenleniyorsa, düzenleme modalını kapat
      if (editingRole?._id === roleId) {
        handleCancelEditRole();
      }
    } catch (err: any) {
      console.error("Delete role error:", err);
      const errorMessage = err.response?.data?.message || 'Rol silinirken bir hata oluştu.';
      setFlashMessage('error', errorMessage);
    } finally {
      setLoadingAction('');
      setIsConfirmModalOpen(false); // Modalı kapat
      // setConfirmModalProps(prev => ({ ...prev, isLoading: false })); // Modal loading state'ini sıfırla
    }
  };

  // Yeni rol adı değiştikçe rol kodunu otomatik güncelle
  useEffect(() => {
    if (isAddingRole) {
      // Türkçe karakterleri ve özel karakterleri temizle, boşlukları _ yap
      const generatedCode = 'ROLE_' + newRoleName
        .toLocaleUpperCase('tr-TR') // Türkçe büyük harf dönüşümü
        .replace(/Ğ/g, 'G')
        .replace(/Ü/g, 'U')
        .replace(/Ş/g, 'S')
        .replace(/İ/g, 'I')
        .replace(/Ö/g, 'O')
        .replace(/Ç/g, 'C')
        .replace(/\s+/g, '_') // Boşlukları _ yap
        .replace(/[^A-Z0-9_]/g, ''); // İzin verilmeyen karakterleri kaldır
      setNewRoleCode(generatedCode);
    }
  }, [newRoleName, isAddingRole]);

  // Filtrelenmiş roller
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(roleSearchQuery.toLowerCase()) ||
    role.roleCode.toLowerCase().includes(roleSearchQuery.toLowerCase())
  );

  // Yetki kontrolü
  if (!isAdmin) {
    return (
      <div className="p-8 bg-red-50 text-red-700 rounded-lg shadow-md flex flex-col items-center">
        {/* ... Erişim Engellendi içeriği ... */}
        <h1 className="text-2xl font-bold mb-2">Erişim Engellendi</h1>
        <p className="text-center">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  // Ana JSX
  return (
    <div className="bg-white shadow-lg rounded-lg p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 pb-2 border-b border-gray-200 flex items-center">
        {/* Güncellenmiş SVG ikonu */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        Rol Yönetimi
      </h1>

      {/* Hata ve Başarı Mesajları */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm flex items-center animate-fade-in" role="alert">
          {/* ... Hata ikonu ve mesajı ... */}
          <p>{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded shadow-sm flex items-center animate-fade-in" role="alert">
          {/* ... Başarı ikonu ve mesajı ... */}
          <p>{success}</p>
        </div>
      )}

      {/* Rol Listesi ve Ekleme Butonu */}
      <div className="mb-10">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Sistemdeki Roller</h2>
            <div className="ml-4 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              {roles.length} rol
            </div>
          </div>

          <div className="flex gap-4 items-center">
            {/* Rol Arama */}
            <div className="relative">
              <input
                type="text"
                placeholder="Rol ara (Ad veya Kod)..."
                value={roleSearchQuery}
                onChange={(e) => setRoleSearchQuery(e.target.value)}
                className="border border-gray-300 rounded-full px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {/* Yeni Rol Ekle Butonu */}
            <button
              onClick={handleAddRole}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition flex items-center shadow-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Yeni Rol Ekle
            </button>
          </div>
        </div>

        {/* Yeni Rol Ekleme Formu (açılır kapanır) */}
        {isAddingRole && (
          <div className="bg-blue-50 p-6 rounded-lg mb-6 border border-blue-200 shadow-inner animate-fade-in">
            <h3 className="text-xl font-semibold text-blue-800 mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Yeni Rol Oluştur
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="newRoleName" className="block text-sm font-medium text-gray-700 mb-1">Rol Adı *</label>
                <input
                  id="newRoleName"
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örn. Satış Yöneticisi"
                  required
                />
              </div>
              <div>
                <label htmlFor="newRoleCode" className="block text-sm font-medium text-gray-700 mb-1">Rol Kodu *</label>
                <input
                  id="newRoleCode"
                  type="text"
                  value={newRoleCode}
                  onChange={(e) => setNewRoleCode(e.target.value)} // Manuel değiştirmeye izin ver
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 font-mono"
                  placeholder="Örn. ROLE_SALES_MANAGER"
                  required
                  disabled
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={createNewRole}
                disabled={loadingAction === 'create-role' || !newRoleName || !newRoleCode}
                className={`bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 transition flex items-center shadow-md ${loadingAction === 'create-role' || !newRoleName || !newRoleCode ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loadingAction === 'create-role' ? (
                  <>
                    <div className="h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Rolü Oluştur
                  </>
                )}
              </button>
              <button
                onClick={() => setIsAddingRole(false)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition"
              >
                İptal
              </button>
            </div>
          </div>
        )}

        {/* Rol Tablosu */}
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          {loading && roles.length === 0 ? (
            <div className="flex justify-center items-center p-10">
              <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center p-6 text-gray-500">
              {roleSearchQuery ? (
                <div className="flex flex-col items-center">
                  {/* ... Arama sonucu yok ikonu ve mesajı ... */}
                  <p>"{roleSearchQuery}" aramasına uygun rol bulunamadı.</p>
                  <button onClick={() => setRoleSearchQuery('')} className="mt-2 text-blue-600 hover:underline">Aramayı temizle</button>
                </div>
              ) : (
                <p>Henüz rol eklenmemiş. Yukarıdaki butonu kullanarak yeni bir rol ekleyebilirsiniz.</p>
              )}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Rol Adı</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Rol Kodu</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">İzinler</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">İşlemler</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRoles.map((role) => (
                  <tr key={role._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{role.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs bg-gray-100 text-gray-800 inline-block px-2 py-1 rounded-md font-mono">{role.roleCode}</div>
                    </td>
                    <td className="px-6 py-4">
                      {/* İzin Özeti */}
                      <div className="flex items-center">
                        {role.permissions && role.permissions.length > 0 ? (
                          <>
                            <div className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2 flex-shrink-0"></div>
                            <span className="text-sm text-gray-600 mr-2">{role.permissions.length} kaynak</span>
                            {/* Detayları hover ile gösterme (isteğe bağlı) */}
                            <div className="group relative inline-block">
                                <button className="text-blue-600 text-xs hover:underline">Detaylar</button>
                                <div className="opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 absolute left-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg p-3 z-20">
                                  <div className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">İzin Detayları</div>
                                  <ul className="space-y-1 max-h-48 overflow-y-auto text-xs">
                                    {role.permissions.map((perm, idx) => (
                                      <li key={idx}>
                                        <span className="font-medium">{allResources.find(r => r.id === perm.resource)?.name || perm.resource}:</span>
                                        <span className="text-gray-600 ml-1">[{perm.actions.join(', ')}]</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                          </>
                        ) : (
                          <>
                            <div className="h-2.5 w-2.5 rounded-full bg-yellow-400 mr-2 flex-shrink-0"></div>
                            <span className="text-xs text-yellow-700">İzin tanımlanmamış</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {/* Düzenle Butonu */}
                      <button
                        onClick={() => handleEditRole(role)}
                        className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md transition-colors inline-flex items-center"
                        title={`${role.name} rolünü düzenle`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Düzenle
                      </button>
                      {/* Sil Butonu */}
                      <button
                        onClick={() => handleDeleteRole(role._id, role.name)} // window.confirm yerine modalı açan fonksiyon
                        disabled={loadingAction.startsWith('delete-')} // Genel silme işlemi sırasında disable edilebilir
                        className={`text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md transition-colors inline-flex items-center ${loadingAction.startsWith('delete-') ? 'opacity-50 cursor-wait' : ''}`}
                        title={`${role.name} rolünü sil`}
                      >
                        {/* Silme butonu içindeki loading state'i kaldırılabilir veya genel loadingAction'a bağlanabilir */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                         </svg>
                        Sil
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Rol Düzenleme Modalı */}
      {isEditingRole && editingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
            {/* Modal Başlığı */}
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Rol Düzenle: <span className="ml-2 font-semibold">{editingRole.name}</span>
              </h2>
              <button
                onClick={handleCancelEditRole}
                className="text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                title="Kapat"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Rol Bilgileri (Adı ve Kodu) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="editRoleName" className="block text-sm font-medium text-gray-700 mb-1">Rol Adı *</label>
                <input
                  id="editRoleName"
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="editRoleCode" className="block text-sm font-medium text-gray-700 mb-1">Rol Kodu</label>
                <input
                  id="editRoleCode"
                  type="text"
                  value={roleCode}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed font-mono"
                  readOnly
                  title="Rol kodu değiştirilemez"
                />
                <p className="text-xs text-gray-500 mt-1">Rol kodu değiştirilemez.</p>
              </div>
            </div>

            {/* İzinler Bölümü */}
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              İzinler
            </h3>
            <div className="space-y-6 bg-gray-50 p-4 rounded-lg shadow-inner border border-gray-200">
              {allResources.map((resource) => (
                <div key={resource.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-700 flex items-center text-base">
                      <span className="mr-2 text-lg">{resource.icon}</span>
                      {resource.name}
                    </h4>
                    <span className="text-xs text-gray-500 italic">{resource.description}</span>
                  </div>
                  {/* Eylemler (Checkbox benzeri yapılar) */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {allActions.map((action) => {
                      const isActive = hasPermission(resource.id, action.id);
                      return (
                        <div
                          key={action.id}
                          className={`
                            p-3 rounded-lg flex items-center justify-between cursor-pointer transition-all duration-200 ease-in-out border
                            ${isActive
                              ? 'bg-green-100 border-green-300 shadow-sm hover:bg-green-200'
                              : 'bg-white border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                            }
                          `}
                          onClick={() => togglePermission(resource.id, action.id)}
                          title={`${resource.name} için ${action.name} iznini ${isActive ? 'kaldır' : 'ver'}`}
                        >
                          <div className="flex items-center">
                            <span className="mr-2 text-lg">{action.icon}</span>
                            <span className={`font-medium text-sm ${isActive ? 'text-green-800' : 'text-gray-700'}`}>
                              {action.name}
                            </span>
                          </div>
                          {/* Checkbox Görünümü */}
                          <div className={`
                            w-5 h-5 rounded-md flex items-center justify-center border transition-colors duration-200
                            ${isActive ? 'bg-green-500 border-green-600 text-white' : 'bg-gray-200 border-gray-300'}
                          `}>
                            {isActive && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Eylemleri (Kaydet, İptal) */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleCancelEditRole}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 py-2 rounded-md transition flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                İptal
              </button>
              <button
                onClick={handleSaveRole}
                disabled={loadingAction === `save-${editingRole._id}`}
                className={`bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition flex items-center shadow-md ${loadingAction === `save-${editingRole._id}` ? 'opacity-50 cursor-wait' : ''}`}
              >
                {loadingAction === `save-${editingRole._id}` ? (
                  <>
                    <div className="h-5 w-5 mr-2 border-t-2 border-b-2 border-white rounded-full animate-spin"></div>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Değişiklikleri Kaydet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onay Modalı */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmModalProps.onConfirm}
        title={confirmModalProps.title}
        message={confirmModalProps.message}
        confirmText={confirmModalProps.confirmText}
        confirmButtonVariant={confirmModalProps.confirmButtonVariant}
        // isLoading={loadingAction.startsWith('delete-')} // Modalın kendi loading state'ini kullanmak isterseniz
      />

      {/* Stil Tanımları */}
      <style jsx>{`
        .loader {
          border-top-color: #3B82F6; /* blue-500 */
          animation: spinner 1.5s linear infinite;
        }
        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default RolesPage;
